import { zodResolver } from '@hookform/resolvers/zod'
import {
  type TrainingSkillItemResponse,
  type UpdateTrainingSkillItemInput,
  UpdateTrainingSkillItemSchema,
} from '@lms/shared'
import { Pencil } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useUpdateTrainingSkillItem } from '@/hooks/use-training-skill-items'

interface SkillItemEditDialogProps {
  trainingId: string
  item: TrainingSkillItemResponse
}

export function SkillItemEditDialog({ trainingId, item }: SkillItemEditDialogProps) {
  const [open, setOpen] = useState(false)
  const updateItem = useUpdateTrainingSkillItem(trainingId)
  const form = useForm<UpdateTrainingSkillItemInput>({
    resolver: zodResolver(UpdateTrainingSkillItemSchema),
  })

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      form.reset({ category: item.category, label: item.label, sortOrder: item.sortOrder })
    }
    setOpen(nextOpen)
  }

  const onSubmit = async (values: UpdateTrainingSkillItemInput) => {
    try {
      await updateItem.mutateAsync({ itemId: item.id, data: values })
      toast.success('項目を更新しました')
      setOpen(false)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '更新に失敗しました')
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" aria-label={`${item.code} を編集`}>
          <Pencil className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>項目 {item.code} を編集</DialogTitle>
          <DialogDescription>
            項目コードは集計で同じ項目として扱うため変更できません。評価中の受講者の項目は変わりません。
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>大項目</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="label"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>具体項目</FormLabel>
                  <FormControl>
                    <Textarea rows={3} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="sortOrder"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>表示順</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={0}
                      className="w-24"
                      {...field}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                キャンセル
              </Button>
              <Button type="submit" disabled={updateItem.isPending}>
                {updateItem.isPending ? '保存中...' : '保存'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
